/**
 * Cliente de voz. Ver docs/05-voice.md, passos 8-12.
 *
 * A sinalização vai num WebSocket PRÓPRIO (`/media`), separado do gateway:
 * é bursty no join e latency-sensitive, e não deve competir com o fan-out
 * de mensagens.
 */
import { Device } from 'mediasoup-client';
import type {
  Consumer,
  Producer,
  RtpCapabilities,
  Transport,
} from 'mediasoup-client/types';
import {
  AUDIO_PROFILE,
  CONSUMER_PRIORITY,
  SCREEN_PROFILES,
  SIMULCAST_ENCODINGS,
  VIDEO_PROFILES,
  VoiceCloseCode,
  rateVoiceQuality,
  spatialLayerForWidth,
  type IceServerConfig,
  type ScreenProfile,
  type VoicePeer,
  type VoiceQuality,
  type VoiceStats,
} from '@tasca/shared';
import { sdk, wsOrigin } from '../config.js';
import { DEFAULT_SETTINGS, vadThresholds, type VoiceSettings } from './settings.js';

export type VoiceConnectionState =
  | 'idle'
  | 'signalling'
  | 'connecting'
  | 'connected'
  | 'failed';

export interface RemoteStream {
  peerId: string;
  displayName: string;
  consumerId: string;
  producerId: string;
  kind: 'audio' | 'video';
  source: string;
  track: MediaStreamTrack;
  stream: MediaStream;
}

export interface AudioDevice {
  deviceId: string;
  label: string;
}

export interface VoiceHandlers {
  onState: (state: VoiceConnectionState, detail?: string) => void;
  onStream: (stream: RemoteStream) => void;
  onStreamEnded: (consumerId: string) => void;
  onPeers: (peers: VoicePeer[]) => void;
  /** Só o próprio: a fala dos outros chega por SPEAKING_UPDATE do gateway. */
  onSpeaking: (peerId: string, speaking: boolean) => void;
  /** Para reenviar pelo gateway. Já vem coalescido — ver `#emitSpeaking`. */
  onLocalSpeaking: (speaking: boolean) => void;
  onQuality: (quality: VoiceQuality, stats: VoiceStats) => void;
  /**
   * Mute, deafen e moderação, sempre que mudam — e também ao ligar ou
   * desligar câmara e ecrã.
   *
   * O cliente é a fonte da verdade destes estados: os botões chamam métodos e
   * o estado volta por aqui. Deixar a UI guardar o seu próprio `muted` dava
   * divergência na primeira vez que outra coisa o mudasse — ensurdecer, que
   * silencia, ou um moderador.
   *
   * Quem ouve isto reenvia o estado ao gateway, e é por aí que os OUTROS
   * sabem que estamos sem microfone ou a partilhar o ecrã: o `producerPaused`
   * do SFU não sai do canal de sinalização.
   */
  onLocalState: (state: {
    muted: boolean;
    deafened: boolean;
    serverMute: boolean;
    serverDeaf: boolean;
  }) => void;
  onSettings: (settings: VoiceSettings) => void;
  onDevices: (devices: {
    inputs: AudioDevice[];
    outputs: AudioDevice[];
    cameras: AudioDevice[];
  }) => void;
  /**
   * Camada e pontuação de um vídeo que estamos a receber.
   *
   * `spatialLayer: null` significa que o mediasoup não está a encaminhar
   * camada nenhuma — banda insuficiente até para a mais baixa. A UI tem de
   * o dizer, senão é um rectângulo preto sem explicação.
   */
  onVideoStatus: (
    consumerId: string,
    status: { spatialLayer?: number | null; score?: number },
  ) => void;
}

interface Pending {
  resolve: (value: unknown) => void;
  reject: (err: Error) => void;
}

/** Mínimo entre dois SPEAKING_UPDATE. Ver `#emitSpeaking`. */
const SPEAKING_FLOOR_MS = 1_000;
const STATS_INTERVAL_MS = 5_000;

export class VoiceClient {
  #socket: WebSocket | null = null;
  #device: Device | null = null;
  #sendTransport: Transport | null = null;
  #recvTransport: Transport | null = null;
  #micProducer: Producer | null = null;
  #camProducer: Producer | null = null;
  #screenProducer: Producer | null = null;
  #screenAudioProducer: Producer | null = null;
  #localStream: MediaStream | null = null;
  #consumers = new Map<string, Consumer>();
  #peerNames = new Map<string, string>();
  #waiters = new Map<string, Pending[]>();
  #state: VoiceConnectionState = 'idle';
  #channelId: string | null = null;
  #vad: { ctx: AudioContext; stop: () => void } | null = null;
  #statsTimer: ReturnType<typeof setInterval> | null = null;

  #settings: VoiceSettings = DEFAULT_SETTINGS;
  #muted = false;
  #deafened = false;
  /** Para restaurar ao desensurdecer: ver `setDeafened`. */
  #mutedBeforeDeafen = false;
  #serverMute = false;
  #serverDeaf = false;

  /** Push-to-talk: a tecla está em baixo agora? */
  #talking = false;
  #keysAttached = false;

  /**
   * O atalho é registado no SO, não no browser.
   *
   * Quando o shell nativo o faz, os listeners de teclado da página saem do
   * caminho: ter os dois activos significava que escrever no composer com a
   * tecla de push-to-talk abriria o microfone, e que um `keyup` perdido num
   * alt-tab seria corrigido pelo global mas não pelo local — dois estados
   * para a mesma coisa.
   */
  #globalPushToTalk = false;

  /** Última fala comunicada ao gateway, e quando. */
  #lastSpeakingSent = false;
  #lastSpeakingAt = 0;
  #speakingTimer: ReturnType<typeof setTimeout> | null = null;

  /** Para calcular o rácio de perda por diferenças. */
  #lastStats: { lost: number; sent: number } | null = null;

  /**
   * Camada pedida por consumidor, para não repetir o pedido.
   *
   * Um redimensionamento com o rato arrastado gera dezenas de eventos de
   * resize por segundo; sem esta memória, cada um deles era um frame de
   * sinalização e o orçamento de 200/10s do SFU fechava o socket.
   */
  #requestedLayer = new Map<string, number>();

  /** Consumidores de vídeo que a UI pausou por estarem fora de vista. */
  #hiddenConsumers = new Set<string>();

  /**
   * `persist` é de quem hospeda.
   *
   * O browser guarda em `localStorage` de forma síncrona, o telemóvel em
   * armazenamento assíncrono. Esconder isso atrás de uma interface comum
   * obrigaria o lado síncrono a fingir que não é, e o SDK não ganha nada com
   * saber onde as preferências ficam — só com saber quais são.
   */
  constructor(
    private readonly handlers: VoiceHandlers,
    options: {
      settings?: VoiceSettings;
      persist?: (settings: VoiceSettings) => void;
    } = {},
  ) {
    if (options.settings) this.#settings = options.settings;
    if (options.persist) this.#persist = options.persist;
  }

  #persist: (settings: VoiceSettings) => void = () => {};

  get state(): VoiceConnectionState {
    return this.#state;
  }

  get channelId(): string | null {
    return this.#channelId;
  }

  get settings(): VoiceSettings {
    return this.#settings;
  }

  get muted(): boolean {
    return this.#muted;
  }

  get deafened(): boolean {
    return this.#deafened;
  }

  get serverMuted(): boolean {
    return this.#serverMute;
  }

  #setState(state: VoiceConnectionState, detail?: string): void {
    this.#state = state;
    this.handlers.onState(state, detail);
  }

  /** Espera por uma mensagem do SFU com um `op` específico. */
  #wait<T>(op: string, timeoutMs = 15_000): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.#drop(op, pending);
        reject(new Error(`timeout à espera de ${op}`));
      }, timeoutMs);

      const pending: Pending = {
        resolve: (value) => {
          clearTimeout(timer);
          resolve(value as T);
        },
        reject: (err) => {
          clearTimeout(timer);
          reject(err);
        },
      };

      const list = this.#waiters.get(op) ?? [];
      list.push(pending);
      this.#waiters.set(op, list);
    });
  }

  #drop(op: string, pending: Pending): void {
    const list = this.#waiters.get(op);
    if (!list) return;
    const i = list.indexOf(pending);
    if (i >= 0) list.splice(i, 1);
  }

  #resolve(op: string, data: unknown): void {
    const list = this.#waiters.get(op);
    if (!list || list.length === 0) return;
    const pending = list.shift()!;
    pending.resolve(data);
  }

  #send(message: unknown): void {
    if (this.#socket?.readyState !== 1) return;
    this.#socket.send(JSON.stringify(message));
  }

  // ─── Dispositivos ─────────────────────────────────────────────────────

  /**
   * Lista microfones e saídas.
   *
   * Só depois de uma permissão concedida é que os `label` vêm preenchidos —
   * antes disso o browser devolve strings vazias de propósito, para não dar
   * uma impressão digital do hardware a qualquer página. Daí esta lista só
   * valer a pena depois do primeiro `getUserMedia`.
   */
  async listDevices(): Promise<{
    inputs: AudioDevice[];
    outputs: AudioDevice[];
    cameras: AudioDevice[];
  }> {
    const inputs: AudioDevice[] = [];
    const outputs: AudioDevice[] = [];
    const cameras: AudioDevice[] = [];
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      for (const d of devices) {
        const entry = { deviceId: d.deviceId, label: d.label || 'Dispositivo sem nome' };
        if (d.kind === 'audioinput') inputs.push(entry);
        else if (d.kind === 'audiooutput') outputs.push(entry);
        else if (d.kind === 'videoinput') cameras.push(entry);
      }
    } catch {
      /* sem permissão ainda: a lista fica vazia */
    }
    const result = { inputs, outputs, cameras };
    this.handlers.onDevices(result);
    return result;
  }

  /**
   * Troca de microfone sem sair do canal.
   *
   * `replaceTrack` e não um produtor novo: renegociar daria um corte
   * audível a todos os outros, e o `produce` acabaria por fechar e abrir o
   * stream no SFU por nada.
   */
  async setInputDevice(deviceId: string | null): Promise<void> {
    this.#patchSettings({ inputDeviceId: deviceId });
    if (!this.#micProducer) return;

    const stream = await this.#openMicrophone();
    const track = stream.getAudioTracks()[0];
    if (!track) return;

    await this.#micProducer.replaceTrack({ track });

    this.#localStream?.getTracks().forEach((t) => t.stop());
    this.#localStream = stream;
    this.#startVad(stream);
  }

  setOutputDevice(deviceId: string | null): void {
    this.#patchSettings({ outputDeviceId: deviceId });
  }

  // ─── Preferências ─────────────────────────────────────────────────────

  #patchSettings(patch: Partial<VoiceSettings>): void {
    this.#settings = { ...this.#settings, ...patch };
    this.#persist(this.#settings);
    this.handlers.onSettings(this.#settings);
  }

  setSensitivity(value: number): void {
    this.#patchSettings({ sensitivity: value });
    // Os limiares são lidos a cada frame, logo não há nada a reiniciar.
  }

  setPushToTalkKey(code: string): void {
    this.#patchSettings({ pttKey: code });
  }

  /**
   * Voz-activada ou push-to-talk.
   *
   * Em PTT o microfone fica em pausa por omissão e só a tecla o abre. É o
   * modo certo em teclado mecânico ou com rede doméstica partilhada; o VAD
   * sozinho apanha sempre alguma coisa.
   */
  async setMode(mode: VoiceSettings['mode']): Promise<void> {
    this.#patchSettings({ mode });
    if (mode === 'ptt') {
      if (!this.#globalPushToTalk) this.#attachKeys();
      await this.#applyMicPause();
    } else {
      this.#detachKeys();
      this.#talking = false;
      await this.#applyMicPause();
    }
  }

  /**
   * O shell nativo assumiu o atalho.
   *
   * Chamado pelo arranque do desktop quando o registo global teve sucesso.
   * Se o SO recusar a tecla (já tomada por outra app), isto fica `false` e o
   * fallback do browser continua a servir com a janela em foco — degradar é
   * melhor que ficar sem push-to-talk nenhum.
   */
  setGlobalPushToTalk(enabled: boolean): void {
    this.#globalPushToTalk = enabled;
    if (enabled) {
      this.#detachKeys();
    } else if (this.#settings.mode === 'ptt' && this.#channelId) {
      this.#attachKeys();
    }
  }

  /** Tecla global premida ou largada. Ver `lib/desktop.ts`. */
  setPushToTalkActive(down: boolean): void {
    if (this.#talking === down) return;
    this.#talking = down;
    void this.#applyMicPause();
  }

  /**
   * Perfil de partilha de ecrã.
   *
   * Só vale para a PRÓXIMA partilha: mudar o perfil a meio exigiria um
   * `getDisplayMedia` novo, e isso significa o diálogo do browser outra vez
   * — pior que esperar pela próxima.
   */
  setScreenProfile(profile: ScreenProfile): void {
    this.#patchSettings({ screenProfile: profile });
  }

  /** Volume de UM participante, 0-100. */
  setPeerVolume(peerId: string, volume: number): void {
    const volumes = { ...this.#settings.volumes, [peerId]: Math.min(100, Math.max(0, volume)) };
    this.#patchSettings({ volumes });
  }

  /**
   * Silenciar uma pessoa só para mim.
   *
   * Pausa o consumer NO SERVIDOR, não só o elemento de áudio: silenciar
   * alguém deve parar de gastar a minha banda, não só de fazer barulho.
   *
   * Só o ÁUDIO. "Silenciar" não é "esconder": quem não quer ouvir alguém
   * continua a querer ver o ecrã que essa pessoa está a partilhar, e apagar
   * o vídeo seria uma surpresa que ninguém pediu.
   */
  setPeerMuted(peerId: string, muted: boolean): void {
    const set = new Set(this.#settings.localMutes);
    if (muted) set.add(peerId);
    else set.delete(peerId);
    this.#patchSettings({ localMutes: [...set] });
    this.#reapplyReceiveState();
  }

  isPeerMuted(peerId: string): boolean {
    return this.#settings.localMutes.includes(peerId);
  }

  // ─── Entrada no canal ─────────────────────────────────────────────────

  /**
   * Entra num canal de voz.
   *
   * `endpoint`, `token` e `iceServers` vêm do VOICE_SERVER_UPDATE que a API
   * enviou pelo gateway — a autorização já aconteceu lá.
   */
  async join(opts: {
    channelId: string;
    endpoint: string;
    token: string;
    iceServers: IceServerConfig[];
  }): Promise<void> {
    await this.leave();

    this.#channelId = opts.channelId;
    this.#setState('signalling');

    // Mesma razao que no gateway: a origem vem da configuracao, nao da pagina.
    const socket = sdk().websocket(`${wsOrigin()}${opts.endpoint}`);
    this.#socket = socket;

    socket.addEventListener('message', (ev) => this.#onMessage(ev));
    socket.addEventListener('close', (ev) => {
      // Um moderador a mover ou a desligar não é uma falha de rede: a UI
      // não deve mostrar "falhou" quando a API já vem a caminho com um
      // token para o canal novo.
      const moderated =
        ev.code === VoiceCloseCode.DISCONNECTED || ev.code === VoiceCloseCode.REPLACED;
      if (this.#state !== 'idle' && !moderated) {
        this.#setState('failed', `sinalização fechou (${ev.code})`);
      }
      this.#cleanup();
      if (moderated) this.#setState('idle');
    });
    socket.addEventListener('error', () => this.#setState('failed', 'erro de sinalização'));

    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('sem hello do SFU em 10s')), 10_000);
      socket.addEventListener('open', () => {
        clearTimeout(timer);
        resolve();
      });
      socket.addEventListener('error', () => {
        clearTimeout(timer);
        reject(new Error('não consegui ligar à sinalização'));
      });
    });

    await this.#wait('hello', 10_000);

    // ─── Device ─────────────────────────────────────────────────────────
    const device = new Device();
    this.#device = device;

    this.#send({ op: 'join', token: opts.token, rtpCapabilities: null });
    const joined = await this.#wait<{
      routerRtpCapabilities: RtpCapabilities;
      peers: VoicePeer[];
    }>('joined');

    await device.load({ routerRtpCapabilities: joined.routerRtpCapabilities });

    for (const peer of joined.peers) this.#peerNames.set(peer.id, peer.displayName);
    this.handlers.onPeers(joined.peers);

    this.#setState('connecting');

    // ─── Transports ─────────────────────────────────────────────────────
    // Dois, separados: permite bitrate e congestão diferentes por direcção
    // e torna óbvio, num getStats, de que lado está o problema.
    this.#sendTransport = await this.#createTransport('send', opts.iceServers);
    this.#recvTransport = await this.#createTransport('recv', opts.iceServers);

    // ─── Microfone ──────────────────────────────────────────────────────
    await this.#startMicrophone();
    if (this.#settings.mode === 'ptt' && !this.#globalPushToTalk) this.#attachKeys();

    // ─── Consumir quem já lá está ───────────────────────────────────────
    for (const peer of joined.peers) {
      for (const producer of peer.producers) {
        await this.#consume(producer.id).catch(() => {
          /* um producer que morreu entretanto não deve abortar o join */
        });
      }
    }

    this.#startStatsProbe();
    this.#setState('connected');
    void this.listDevices();
  }

  async #createTransport(
    direction: 'send' | 'recv',
    iceServers: IceServerConfig[],
  ): Promise<Transport> {
    this.#send({ op: 'createTransport', direction });
    const info = await this.#wait<{
      id: string;
      iceParameters: never;
      iceCandidates: never;
      dtlsParameters: never;
    }>('transportCreated');

    const options = {
      id: info.id,
      iceParameters: info.iceParameters,
      iceCandidates: info.iceCandidates,
      dtlsParameters: info.dtlsParameters,
      iceServers: iceServers as RTCIceServer[],
    };

    const transport =
      direction === 'send'
        ? this.#device!.createSendTransport(options)
        : this.#device!.createRecvTransport(options);

    transport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this.#send({ op: 'connectTransport', transportId: transport.id, dtlsParameters });
      this.#wait('transportConnected')
        .then(() => callback())
        .catch((err: Error) => errback(err));
    });

    if (direction === 'send') {
      transport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
        this.#send({
          op: 'produce',
          transportId: transport.id,
          kind,
          rtpParameters,
          source: (appData as { source?: string }).source ?? 'mic',
        });
        this.#wait<{ producerId: string }>('produced')
          .then((r) => callback({ id: r.producerId }))
          .catch((err: Error) => errback(err));
      });
    }

    transport.on('connectionstatechange', (state) => {
      if (state === 'failed') {
        // ICE falhou: tentar restart antes de desistir. Numa mudança de
        // rede (Wi-Fi para 4G) isto recupera sem sair do canal.
        this.#send({ op: 'restartIce', transportId: transport.id });
      }
      if (state === 'disconnected' && this.#state === 'connected') {
        this.#setState('connecting', 'ligação de media interrompida');
      }
      if (state === 'connected' && this.#state === 'connecting') {
        this.#setState('connected');
      }
    });

    return transport;
  }

  async #openMicrophone(): Promise<MediaStream> {
    // Cancelamento de eco, supressão de ruído e AGC no CLIENTE. Nunca no
    // servidor: isso obrigaria a descodificar, e um SFU não descodifica.
    return navigator.mediaDevices.getUserMedia({
      audio: {
        ...(this.#settings.inputDeviceId
          ? { deviceId: { exact: this.#settings.inputDeviceId } }
          : {}),
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: AUDIO_PROFILE.channels,
        sampleRate: AUDIO_PROFILE.sampleRate,
      },
      video: false,
    });
  }

  async #startMicrophone(): Promise<void> {
    let stream: MediaStream;
    try {
      stream = await this.#openMicrophone();
    } catch (err) {
      // Um deviceId guardado que já não existe (auscultadores desligados) dá
      // OverconstrainedError. Cair para o dispositivo por omissão é melhor
      // que recusar a entrada no canal.
      if (this.#settings.inputDeviceId) {
        this.#patchSettings({ inputDeviceId: null });
        stream = await this.#openMicrophone();
      } else {
        throw err;
      }
    }
    this.#localStream = stream;

    const track = stream.getAudioTracks()[0];
    if (!track) throw new Error('sem pista de áudio');

    this.#micProducer = await this.#sendTransport!.produce({
      track,
      codecOptions: {
        opusStereo: false,
        opusDtx: AUDIO_PROFILE.dtx,
        opusFec: AUDIO_PROFILE.fec,
        opusPtime: AUDIO_PROFILE.ptime,
        opusMaxAverageBitrate: AUDIO_PROFILE.bitrate,
      },
      appData: { source: 'mic' },
    });

    await this.#applyMicPause();
    this.#startVad(stream);
  }

  /**
   * O microfone transmite? É a conjunção de quatro coisas, e tê-la num só
   * sítio é o que evita que um bug de UI deixe alguém a transmitir sem saber.
   */
  #micShouldSend(): boolean {
    if (this.#muted || this.#serverMute) return false;
    if (this.#settings.mode === 'ptt') return this.#talking;
    return true;
  }

  async #applyMicPause(): Promise<void> {
    const producer = this.#micProducer;
    if (!producer) return;
    const paused = !this.#micShouldSend();
    if (producer.paused === paused) return;

    if (paused) await producer.pause();
    else await producer.resume();
    this.#send({ op: 'pauseProducer', producerId: producer.id, paused });
    if (paused) {
      this.handlers.onSpeaking('self', false);
      this.#emitSpeaking(false);
    }
  }

  // ─── Push-to-talk ─────────────────────────────────────────────────────

  #onKeyDown = (ev: KeyboardEvent): void => {
    if (ev.code !== this.#settings.pttKey || ev.repeat) return;
    // Num campo de texto a tecla é texto, não push-to-talk.
    const target = ev.target as HTMLElement | null;
    if (target?.closest('input, textarea, [contenteditable="true"]')) return;
    if (this.#settings.pttKey === 'Space') ev.preventDefault();
    this.#talking = true;
    void this.#applyMicPause();
  };

  #onKeyUp = (ev: KeyboardEvent): void => {
    if (ev.code !== this.#settings.pttKey) return;
    this.#talking = false;
    void this.#applyMicPause();
  };

  /**
   * Perder o foco da janela larga a tecla.
   *
   * Sem isto, um alt-tab com a tecla em baixo deixava o microfone aberto
   * indefinidamente — o `keyup` acontece noutra janela e nunca chega aqui.
   * É o bug clássico de push-to-talk no browser, e é a razão de a Fase 7
   * (Tauri, hotkey global) existir.
   */
  #onBlur = (): void => {
    if (!this.#talking) return;
    this.#talking = false;
    void this.#applyMicPause();
  };

  /**
   * O fallback de teclado só existe onde há teclado.
   *
   * Num telemóvel não há `window.addEventListener` nem teclas para premir: o
   * push-to-talk é um botão no ecrã que chama `setPushToTalkActive`. Sem esta
   * guarda, o SDK rebentava no arranque da app mobile.
   */
  #attachKeys(): void {
    if (this.#keysAttached) return;
    if (typeof window === 'undefined' || !window.addEventListener) return;
    window.addEventListener('keydown', this.#onKeyDown);
    window.addEventListener('keyup', this.#onKeyUp);
    window.addEventListener('blur', this.#onBlur);
    this.#keysAttached = true;
  }

  #detachKeys(): void {
    if (!this.#keysAttached) return;
    window.removeEventListener('keydown', this.#onKeyDown);
    window.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('blur', this.#onBlur);
    this.#keysAttached = false;
  }

  // ─── Deteção de fala ──────────────────────────────────────────────────

  /**
   * Deteção de fala no cliente, para o anel verde na UI.
   *
   * Não controla o envio de media — isso é o DTX do Opus. Com histerese
   * (limiares de entrada e saída diferentes, ver settings.ts) para o
   * indicador não piscar em cada pausa entre palavras.
   */
  #startVad(stream: MediaStream): void {
    this.#vad?.stop();
    this.#vad = null;
    // Web Audio não existe no React Native. Sem ele não há anel verde para o
    // próprio — a voz funciona na mesma, e a fala dos outros continua a vir
    // por SPEAKING_UPDATE do gateway.
    if (typeof AudioContext === 'undefined') return;
    try {
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);

      const data = new Uint8Array(analyser.frequencyBinCount);
      let speaking = false;
      let quietFrames = 0;

      const tick = (): void => {
        // Em pausa não há fala a indicar, por muito barulho que haja na sala.
        if (!this.#micShouldSend()) {
          if (speaking) {
            speaking = false;
            this.handlers.onSpeaking('self', false);
            this.#emitSpeaking(false);
          }
          return;
        }

        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (const v of data) sum += v * v;
        const rms = Math.sqrt(sum / data.length);
        const { enter, exit } = vadThresholds(this.#settings.sensitivity);

        if (!speaking && rms > enter) {
          speaking = true;
          quietFrames = 0;
          this.handlers.onSpeaking('self', true);
          this.#emitSpeaking(true);
        } else if (speaking && rms < exit) {
          quietFrames += 1;
          // ~400ms de silêncio antes de apagar o indicador.
          if (quietFrames > 8) {
            speaking = false;
            this.handlers.onSpeaking('self', false);
            this.#emitSpeaking(false);
          }
        } else if (speaking) {
          quietFrames = 0;
        }
      };

      const timer = setInterval(tick, 50);
      this.#vad = {
        ctx,
        stop: () => {
          clearInterval(timer);
          void ctx.close();
        },
      };
    } catch {
      /* sem AudioContext: a voz funciona, só não há indicador */
    }
  }

  /**
   * Comunica a fala ao gateway, no máximo uma vez por segundo.
   *
   * O orçamento de frames do gateway é de 120/minuto para TUDO — heartbeats,
   * escrita, subscrições. Uma conversa animada alterna fala e silêncio mais
   * depressa que isso, logo sem este piso o próprio indicador fechava o
   * socket por rate limit. O último valor é sempre enviado no fim da janela,
   * nunca perdido.
   */
  #emitSpeaking(speaking: boolean): void {
    if (speaking === this.#lastSpeakingSent) {
      if (this.#speakingTimer) {
        clearTimeout(this.#speakingTimer);
        this.#speakingTimer = null;
      }
      return;
    }

    const since = Date.now() - this.#lastSpeakingAt;
    if (since >= SPEAKING_FLOOR_MS) {
      this.#lastSpeakingSent = speaking;
      this.#lastSpeakingAt = Date.now();
      this.handlers.onLocalSpeaking(speaking);
      return;
    }

    if (this.#speakingTimer) return;
    this.#speakingTimer = setTimeout(() => {
      this.#speakingTimer = null;
      // Reavaliar no fim da espera: o estado pode já ter voltado atrás, e aí
      // não há nada para enviar.
      const current = this.#micShouldSend() ? speaking : false;
      if (current === this.#lastSpeakingSent) return;
      this.#lastSpeakingSent = current;
      this.#lastSpeakingAt = Date.now();
      this.handlers.onLocalSpeaking(current);
    }, SPEAKING_FLOOR_MS - since);
  }

  // ─── Qualidade de ligação ─────────────────────────────────────────────

  /**
   * Sonda regular de qualidade.
   *
   * 5s é um compromisso: mais depressa e o indicador fica nervoso com um
   * pico isolado, mais devagar e só se sabe que a chamada está má depois de
   * já estar. O SFU aproveita estes pedidos para alimentar o histograma de
   * RTT — ver apps/sfu/src/metrics.ts.
   */
  #startStatsProbe(): void {
    this.#stopStatsProbe();
    this.#lastStats = null;
    this.#statsTimer = setInterval(() => {
      if (this.#socket?.readyState === 1) this.#send({ op: 'getStats' });
    }, STATS_INTERVAL_MS);
  }

  #stopStatsProbe(): void {
    if (this.#statsTimer) clearInterval(this.#statsTimer);
    this.#statsTimer = null;
  }

  #onStats(stats: VoiceStats): void {
    const lost = stats.packetsLost ?? 0;
    const sent = stats.packetsSent ?? 0;

    // Rácio pela DIFERENÇA: os contadores são cumulativos desde o join, e
    // usá-los em bruto diluía uma queda recente em dez minutos de chamada
    // boa — exactamente quando o indicador tinha de acender.
    let ratio = 0;
    if (this.#lastStats) {
      const dLost = Math.max(0, lost - this.#lastStats.lost);
      const dSent = Math.max(0, sent - this.#lastStats.sent);
      if (dSent + dLost > 0) ratio = dLost / (dSent + dLost);
    }
    this.#lastStats = { lost, sent };

    this.handlers.onQuality(rateVoiceQuality(stats.rtt, ratio), stats);
  }

  // ─── Consumo ──────────────────────────────────────────────────────────

  async #consume(producerId: string): Promise<void> {
    if (!this.#recvTransport || !this.#device) return;

    this.#send({
      op: 'consume',
      transportId: this.#recvTransport.id,
      producerId,
      rtpCapabilities: this.#device.rtpCapabilities,
    });

    const info = await this.#wait<{
      consumerId: string;
      producerId: string;
      kind: 'audio' | 'video';
      rtpParameters: never;
      peerId: string;
      source: string;
    }>('consumed');

    const consumer = await this.#recvTransport.consume({
      id: info.consumerId,
      producerId: info.producerId,
      kind: info.kind,
      rtpParameters: info.rtpParameters,
      // Guardar o peerId AQUI: e por ele que o `peerLeft` encontra os
      // consumers a fechar. Sem isto ficavam streams fantasma a tocar.
      appData: { peerId: info.peerId, source: info.source },
    });

    this.#consumers.set(consumer.id, consumer);

    const stream = new MediaStream([consumer.track]);
    this.handlers.onStream({
      peerId: info.peerId,
      displayName: this.#peerNames.get(info.peerId) ?? 'Utilizador',
      consumerId: consumer.id,
      producerId: info.producerId,
      kind: info.kind,
      source: info.source,
      track: consumer.track,
      stream,
    });

    // RESUME só DEPOIS de o track estar ligado ao elemento de áudio. O
    // consumer nasce em pausa no servidor precisamente para isto: despausar
    // antes perde os primeiros pacotes e corta a primeira sílaba.
    //
    // Ensurdecido, ou com esta pessoa silenciada, fica em pausa — e assim
    // nem a banda se gasta.
    const mutedPeer = info.kind === 'audio' && this.isPeerMuted(info.peerId);
    if (!this.#deafened && !mutedPeer) {
      this.#send({ op: 'resumeConsumer', consumerId: consumer.id });
    }
  }

  #onMessage(ev: MessageEvent<string>): void {
    let message: { op: string } & Record<string, unknown>;
    try {
      message = JSON.parse(ev.data) as { op: string } & Record<string, unknown>;
    } catch {
      return;
    }

    switch (message.op) {
      case 'newProducer': {
        void this.#consume(message['producerId'] as string).catch(() => {});
        return;
      }

      case 'peerJoined': {
        const peer = message['peer'] as VoicePeer;
        this.#peerNames.set(peer.id, peer.displayName);
        this.handlers.onPeers([peer]);
        return;
      }

      case 'peerLeft': {
        const peerId = message['peerId'] as string;
        for (const [id, consumer] of this.#consumers) {
          if (consumer.appData['peerId'] === peerId) {
            consumer.close();
            this.#consumers.delete(id);
            this.handlers.onStreamEnded(id);
          }
        }
        this.#peerNames.delete(peerId);
        return;
      }

      case 'producerClosed': {
        const producerId = message['producerId'] as string;
        for (const [id, consumer] of this.#consumers) {
          if (consumer.producerId === producerId) {
            consumer.close();
            this.#consumers.delete(id);
            this.handlers.onStreamEnded(id);
          }
        }
        return;
      }

      case 'producerPaused': {
        this.handlers.onSpeaking(
          message['peerId'] as string,
          !(message['paused'] as boolean),
        );
        return;
      }

      case 'moderated': {
        this.#serverMute = message['serverMute'] === true;
        this.#serverDeaf = message['serverDeaf'] === true;
        void this.#applyMicPause();
        // Levantar um server deafen despausa TODOS os consumers do lado do
        // SFU — incluindo os que este cliente tinha pausado por vontade
        // própria. Sem reaplicar, um moderador generoso desfazia o deafen e
        // os mutes locais de quem nem pediu.
        this.#reapplyReceiveState();
        this.#emitLocalState();
        return;
      }

      case 'consumerLayersChanged': {
        this.handlers.onVideoStatus(message['consumerId'] as string, {
          spatialLayer: message['spatialLayer'] as number | null,
        });
        return;
      }

      case 'consumerScore': {
        this.handlers.onVideoStatus(message['consumerId'] as string, {
          score: message['score'] as number,
        });
        return;
      }

      case 'stats': {
        this.#onStats(message['stats'] as VoiceStats);
        return;
      }

      case 'error': {
        const code = message['code'] as string;
        // Um erro de uma operação pendente tem de a rejeitar, senão o join
        // fica pendurado até ao timeout.
        for (const [, list] of this.#waiters) {
          const pending = list.shift();
          if (pending) {
            pending.reject(new Error(code));
            break;
          }
        }
        this.#setState('failed', code);
        return;
      }

      default:
        this.#resolve(message.op, message);
        return;
    }
  }

  // ─── Controlos ────────────────────────────────────────────────────────

  #emitLocalState(): void {
    this.handlers.onLocalState({
      muted: this.#muted,
      deafened: this.#deafened,
      serverMute: this.#serverMute,
      serverDeaf: this.#serverDeaf,
    });
  }

  async setMuted(muted: boolean): Promise<void> {
    this.#muted = muted;
    await this.#applyMicPause();
    this.#emitLocalState();
  }

  /**
   * Ensurdecer: parar de receber tudo.
   *
   * Pausa os consumers no SERVIDOR, não só localmente — num canal de 20
   * pessoas isso corta 19 streams de downstream. E arrasta o microfone
   * consigo: ouvir ninguém e continuar a falar é a receita para falar para
   * o vazio.
   */
  /**
   * Volta a impor o que ESTE cliente decidiu sobre o que recebe.
   *
   * Existe porque o estado de pausa de um consumer é do servidor e tem duas
   * origens: a moderação e as escolhas locais. Quando a moderação mexe,
   * reaplicar as locais é o que evita que uma anule a outra.
   */
  #reapplyReceiveState(): void {
    for (const [id, consumer] of this.#consumers) {
      const peerId = consumer.appData['peerId'] as string | undefined;
      // Cada tipo tem a sua razão para estar em pausa, e confundi-las
      // produz bugs nos dois sentidos: ensurdecer apaga tudo, silenciar
      // alguém cala só o áudio dessa pessoa, e estar fora de vista pausa só
      // o vídeo.
      const shouldPause =
        this.#deafened ||
        (consumer.kind === 'audio' && peerId !== undefined && this.isPeerMuted(peerId)) ||
        (consumer.kind === 'video' && this.#hiddenConsumers.has(id));
      this.#send({ op: shouldPause ? 'pauseConsumer' : 'resumeConsumer', consumerId: id });
    }
  }

  async setDeafened(deafened: boolean): Promise<void> {
    if (deafened === this.#deafened) return;
    this.#deafened = deafened;
    this.#reapplyReceiveState();

    if (deafened) {
      this.#mutedBeforeDeafen = this.#muted;
      this.#muted = true;
    } else {
      // Devolver o microfone a quem o tinha ligado, e deixá-lo desligado a
      // quem já o tinha desligado antes de ensurdecer.
      this.#muted = this.#mutedBeforeDeafen;
    }
    await this.#applyMicPause();
    this.#emitLocalState();
  }

  async #openCamera(): Promise<MediaStream> {
    const profile = VIDEO_PROFILES[this.#settings.videoProfile];
    return navigator.mediaDevices.getUserMedia({
      video: {
        ...(this.#settings.cameraDeviceId
          ? { deviceId: { exact: this.#settings.cameraDeviceId } }
          : {}),
        // `ideal` e não `exact`: uma webcam que não faz 1280x720 deve dar o
        // que consegue, não lançar OverconstrainedError e deixar a pessoa
        // sem câmara nenhuma.
        width: { ideal: profile.width },
        height: { ideal: profile.height },
        frameRate: { ideal: profile.frameRate },
      },
    });
  }

  async startCamera(): Promise<void> {
    if (this.#camProducer || !this.#sendTransport) return;

    let stream: MediaStream;
    try {
      stream = await this.#openCamera();
    } catch (err) {
      // Câmara guardada que já não existe: cair para a predefinida é melhor
      // que recusar ligar a câmara.
      if (this.#settings.cameraDeviceId) {
        this.#patchSettings({ cameraDeviceId: null });
        stream = await this.#openCamera();
      } else {
        throw err;
      }
    }

    const track = stream.getVideoTracks()[0];
    if (!track) return;
    // Diz ao codificador que isto é movimento: prefere fluidez a nitidez.
    track.contentHint = 'motion';

    this.#camProducer = await this.#sendTransport.produce({
      track,
      // Simulcast: o SFU escolhe a camada por consumidor. Sem isto, um
      // participante com rede fraca degrada a qualidade de todos — ver
      // SIMULCAST_ENCODINGS em packages/shared.
      encodings: [...SIMULCAST_ENCODINGS],
      codecOptions: { videoGoogleStartBitrate: 500 },
      appData: { source: 'cam' },
    });
    this.#emitLocalState();
  }

  /** Troca de câmara sem renegociar — como no microfone. */
  async setCameraDevice(deviceId: string | null): Promise<void> {
    this.#patchSettings({ cameraDeviceId: deviceId });
    if (!this.#camProducer) return;
    const stream = await this.#openCamera();
    const track = stream.getVideoTracks()[0];
    if (!track) return;
    track.contentHint = 'motion';
    const old = this.#camProducer.track;
    await this.#camProducer.replaceTrack({ track });
    old?.stop();
  }

  /**
   * Resolução de captura.
   *
   * Exige recomeçar o produtor: as camadas de simulcast derivam da
   * resolução de origem, logo mudá-la é mudar as três. `replaceTrack` com
   * outra resolução deixaria os `scaleResolutionDownBy` a apontar para
   * tamanhos que já não existem.
   */
  async setVideoProfile(profile: VoiceSettings['videoProfile']): Promise<void> {
    this.#patchSettings({ videoProfile: profile });
    if (!this.#camProducer) return;
    await this.stopCamera();
    await this.startCamera();
  }

  async stopCamera(): Promise<void> {
    if (!this.#camProducer) return;
    this.#send({ op: 'closeProducer', producerId: this.#camProducer.id });
    this.#camProducer.track?.stop();
    this.#camProducer.close();
    this.#camProducer = null;
    this.#emitLocalState();
  }

  get cameraOn(): boolean {
    return this.#camProducer !== null;
  }

  get screenOn(): boolean {
    return this.#screenProducer !== null;
  }

  /**
   * As faixas que ESTAMOS a enviar, para a própria pessoa se ver.
   *
   * O que sai daqui nunca volta pelo SFU — ninguém consome o seu próprio
   * producer — logo sem isto quem liga a câmara é o único que não sabe o
   * que os outros estão a ver. São as faixas vivas, não cópias: quem as usa
   * monta um MediaStream e não lhes mexe.
   */
  get cameraTrack(): MediaStreamTrack | null {
    return this.#camProducer?.track ?? null;
  }

  get screenTrack(): MediaStreamTrack | null {
    return this.#screenProducer?.track ?? null;
  }

  /** O ecrã está a partilhar som? Só Chromium o permite, e só em separadores. */
  get screenAudioOn(): boolean {
    return this.#screenAudioProducer !== null;
  }

  /**
   * Partilha de ecrã.
   *
   * Dois perfis, e a diferença não é cosmética: 1080p a 5fps mantém uma
   * linha de código legível; 720p a 30fps mantém um vídeo fluido. Pedir os
   * dois ao mesmo tempo é pedir 1080p30, que nesta VPS não cabe no orçamento
   * de banda (ver docs/05).
   *
   * Sem simulcast no ecrã, de propósito: a camada baixa de um texto a 1080p
   * é ilegível, logo não serviria a ninguém, e as três camadas custariam
   * 1.3x a banda para nada.
   */
  async startScreenShare(profileName?: ScreenProfile): Promise<void> {
    if (this.#screenProducer || !this.#sendTransport) return;
    const name = profileName ?? this.#settings.screenProfile;
    const profile = SCREEN_PROFILES[name];

    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        width: { ideal: profile.width },
        height: { ideal: profile.height },
        frameRate: { ideal: profile.frameRate },
      },
      // Áudio do separador, onde o browser o permite (Chromium, partilha de
      // tab). Pedir e não receber é o caso normal e não é erro.
      audio: true,
    });

    const track = stream.getVideoTracks()[0];
    if (!track) return;
    // 'detail' preserva nitidez em vez de suavizar movimento — é a
    // diferença entre ler e não ler o código do outro lado.
    track.contentHint = profile.contentHint;

    // O utilizador pode parar a partilha pelo próprio browser.
    track.addEventListener('ended', () => void this.stopScreenShare());

    this.#screenProducer = await this.#sendTransport.produce({
      track,
      encodings: [{ maxBitrate: profile.bitrate }],
      appData: { source: 'screen' },
    });

    const audioTrack = stream.getAudioTracks()[0];
    if (audioTrack) {
      // Áudio de sistema é música e efeitos, não fala: estéreo, bitrate
      // alto, e DTX DESLIGADO — cortar em silêncio num tema musical
      // introduz buracos audíveis, que é o contrário do que o DTX faz numa
      // conversa.
      this.#screenAudioProducer = await this.#sendTransport.produce({
        track: audioTrack,
        codecOptions: {
          opusStereo: true,
          opusDtx: false,
          opusFec: true,
          opusMaxAverageBitrate: 128_000,
        },
        appData: { source: 'screen' },
      });
    }

    this.#patchSettings({ screenProfile: name });
    this.#emitLocalState();
  }

  async stopScreenShare(): Promise<void> {
    for (const producer of [this.#screenProducer, this.#screenAudioProducer]) {
      if (!producer) continue;
      this.#send({ op: 'closeProducer', producerId: producer.id });
      producer.track?.stop();
      producer.close();
    }
    if (!this.#screenProducer && !this.#screenAudioProducer) return;
    this.#screenProducer = null;
    this.#screenAudioProducer = null;
    this.#emitLocalState();
  }

  // ─── Adaptação de camada ──────────────────────────────────────────────

  /**
   * Pede a camada adequada ao tamanho a que o vídeo está REALMENTE a ser
   * mostrado.
   *
   * É o ponto todo do simulcast: um tile de 160px a receber 720p gasta banda
   * de todos em pixels que ninguém vê. A decisão é do cliente porque só ele
   * sabe o tamanho de render — o servidor vê bytes, não layout.
   */
  requestLayerForWidth(consumerId: string, cssWidth: number): void {
    const consumer = this.#consumers.get(consumerId);
    if (!consumer || consumer.kind !== 'video') return;
    if (consumer.appData['source'] === 'screen') return; // ecrã não tem camadas

    const ratio = typeof window !== 'undefined' ? (window.devicePixelRatio ?? 1) : 1;
    const pixels = cssWidth * ratio;
    const layer = spatialLayerForWidth(pixels);
    if (this.#requestedLayer.get(consumerId) === layer) return;
    this.#requestedLayer.set(consumerId, layer);
    this.#send({ op: 'setConsumerLayer', consumerId, spatialLayer: layer });
  }

  /**
   * Pausa ou retoma UM vídeo, por estar fora de vista.
   *
   * No servidor, não só no elemento: um tile fora do ecrã, ou num separador
   * em segundo plano, não deve gastar banda nenhuma. É a optimização de
   * maior retorno do vídeo, equivalente ao que o DTX é no áudio.
   */
  setVideoHidden(consumerId: string, hidden: boolean): void {
    const consumer = this.#consumers.get(consumerId);
    if (!consumer || consumer.kind !== 'video') return;
    if (hidden === this.#hiddenConsumers.has(consumerId)) return;

    if (hidden) {
      this.#hiddenConsumers.add(consumerId);
      this.#send({ op: 'pauseConsumer', consumerId });
    } else {
      this.#hiddenConsumers.delete(consumerId);
      if (!this.#deafened) this.#send({ op: 'resumeConsumer', consumerId });
    }
  }

  /** Dá a um vídeo prioridade de foco: o mediasoup corta-o em último. */
  focusVideo(consumerId: string | null): void {
    for (const [id, consumer] of this.#consumers) {
      if (consumer.kind !== 'video') continue;
      const source = consumer.appData['source'] === 'screen' ? 'screen' : 'cam';
      const priority = id === consumerId ? 255 : CONSUMER_PRIORITY[source];
      this.#send({ op: 'setConsumerPriority', consumerId: id, priority });
    }
  }

  async leave(): Promise<void> {
    if (this.#socket?.readyState === 1) this.#send({ op: 'leave' });
    this.#cleanup();
    this.#setState('idle');
  }

  #cleanup(): void {
    this.#stopStatsProbe();
    this.#detachKeys();
    this.#talking = false;

    if (this.#speakingTimer) clearTimeout(this.#speakingTimer);
    this.#speakingTimer = null;
    this.#lastSpeakingSent = false;
    this.#lastSpeakingAt = 0;

    this.#vad?.stop();
    this.#vad = null;

    for (const consumer of this.#consumers.values()) {
      try {
        consumer.close();
      } catch {
        /* já fechado */
      }
    }
    this.#consumers.clear();

    for (const producer of [
      this.#micProducer,
      this.#camProducer,
      this.#screenProducer,
      this.#screenAudioProducer,
    ]) {
      try {
        producer?.track?.stop();
        producer?.close();
      } catch {
        /* já fechado */
      }
    }
    this.#micProducer = null;
    this.#camProducer = null;
    this.#screenProducer = null;
    this.#screenAudioProducer = null;
    this.#requestedLayer.clear();
    this.#hiddenConsumers.clear();

    this.#localStream?.getTracks().forEach((t) => t.stop());
    this.#localStream = null;

    try {
      this.#sendTransport?.close();
      this.#recvTransport?.close();
    } catch {
      /* já fechado */
    }
    this.#sendTransport = null;
    this.#recvTransport = null;
    this.#device = null;

    for (const [, list] of this.#waiters) {
      for (const pending of list) pending.reject(new Error('sessão de voz terminou'));
    }
    this.#waiters.clear();
    this.#peerNames.clear();

    try {
      this.#socket?.close(1000);
    } catch {
      /* já fechado */
    }
    this.#socket = null;

    // A ordem importa: `channelId` a null ANTES de anunciar o estado. Quem
    // ouve o estado reenvia-o ao gateway, e um anúncio com o canal ainda
    // preenchido dizia à API "continuo aqui, só sem microfone" no momento
    // exacto em que estamos a sair dele.
    this.#channelId = null;

    // A moderação não é estado do cliente: vem outra vez no token do
    // próximo join. Limpar aqui evita arrastar um mute para um canal onde
    // ele já não se aplica.
    this.#serverMute = false;
    this.#serverDeaf = false;
    this.#emitLocalState();
  }
}

/** O browser suporta o que precisamos? */
export function voiceSupported(): { ok: boolean; reason?: string } {
  // `isSecureContext` é um conceito de browser. No React Native não existe e
  // a questão não se põe: não há origem insegura de onde a app possa correr.
  if (typeof window !== 'undefined' && 'isSecureContext' in window && !window.isSecureContext) {
    return {
      ok: false,
      reason: 'a voz exige HTTPS (contexto seguro) para acesso ao microfone',
    };
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return { ok: false, reason: 'este browser não dá acesso ao microfone' };
  }
  // `canProduce` e metodo de INSTANCIA e precisa de um device carregado,
  // logo nao serve como teste de suporte. A presenca de RTCPeerConnection e
  // o sinal certo nesta fase.
  if (typeof RTCPeerConnection === 'undefined') {
    return { ok: false, reason: 'WebRTC indisponível neste browser' };
  }
  return { ok: true };
}

/**
 * O browser consegue partilhar o ecrã?
 *
 * `getDisplayMedia` não existe em contextos inseguros nem na maioria dos
 * browsers móveis. Mostrar o botão e falhar depois é pior que não o mostrar.
 */
export function screenShareSupported(): boolean {
  return typeof navigator.mediaDevices?.getDisplayMedia === 'function';
}

/**
 * O browser deixa escolher a saída de áudio?
 *
 * `setSinkId` é Chromium-only à data. No Firefox e no Safari a saída é a do
 * sistema e o selector não deve aparecer — pior que não ter a opção é ter
 * uma que não faz nada.
 */
export function outputSelectionSupported(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

/**
 * Partilha de ecrã: além de existir, tem de ser possível.
 *
 * Em iOS e Android não há `getDisplayMedia` — a partilha de ecrã móvel passa
 * por extensões de sistema (ReplayKit, MediaProjection) e é um projecto
 * próprio. `screenShareSupported` já devolve `false` lá, e é a resposta certa.
 */
